import React, { type ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material'

import {
  Banner,
  Button,
  Icon,
  IconButton,
  Table,
  type TableHeader,
  TextField,
  Typography,
} from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { useCheckCategoryEvaluationConfigMutation } from '~redux/slices/apiSlice'
import { typography } from '~tokens/internal'
import { toTitleCase } from '~utils/textUtils'

import TableToolbar from '../Table/TableToolbar'

const MAX_TEST_QUERIES = 20

type SaveStep = 'queries' | 'results' | 'confirm'

type CategorizedQueryRow = {
  id: number
  query: string
  category: string
}

export interface AICategoriesSaveDialogProps {
  open: boolean
  onClose: () => void
  organizationIdentityId: string
  categories: { name: string; definition?: string }[]
  saving: boolean
  canBackfill: boolean
  onSave: () => Promise<void>
}

const categorizedQueryHeaders: TableHeader<CategorizedQueryRow>[] = [
  { id: 'query', label: 'Query', render: (row) => row.query },
  { id: 'category', label: 'Category', render: (row) => toTitleCase(row.category) },
]

const AICategoriesSaveDialog: React.FC<AICategoriesSaveDialogProps> = ({
  open,
  onClose,
  organizationIdentityId,
  categories,
  saving,
  canBackfill,
  onSave,
}): ReactElement => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const [
    checkCategories,
    { data: checkResult, isLoading: checking, isError: checkFailed, reset: resetCheck },
  ] = useCheckCategoryEvaluationConfigMutation()

  const [step, setStep] = useState<SaveStep>('queries')
  const [queryInputs, setQueryInputs] = useState<string[]>([''])

  const queries = queryInputs.map((query) => query.trim()).filter(Boolean)

  const updateQuery = (index: number, value: string): void => {
    setQueryInputs((current) => current.map((query, i) => (i === index ? value : query)))
  }

  const handleAddQuery = (): void => {
    setQueryInputs((current) => [...current, ''])
  }

  const handleRemoveQuery = (index: number): void => {
    setQueryInputs((current) => current.filter((_, i) => i !== index))
  }

  const queryHeaders: TableHeader<{ id: number; query: string }>[] = [
    {
      id: 'query',
      label: 'Test Query',
      render: (row, rowIndex) => (
        <TextField
          size='small'
          containerStyle={{ width: '100%' }}
          placeholder='e.g. How do I renew my business license?'
          value={row.query}
          onChange={(event) => updateQuery(rowIndex, event.target.value)}
          disabled={checking}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (_, rowIndex) => (
        <Tooltip title='Remove query'>
          <IconButton
            aria-label='Remove query'
            onClick={() => handleRemoveQuery(rowIndex)}
            disabled={checking}
          >
            <Icon name='delete' size={typography.fontSizePx.md} />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const handleRunTest = async (): Promise<void> => {
    if (!userId) return
    try {
      await checkCategories({
        user_id: userId,
        organization_identity_id: organizationIdentityId,
        categories,
        queries,
      }).unwrap()
      setStep('results')
    } catch {
      // Error is shown in a banner above the queries via `checkFailed`
    }
  }

  const handleClose = (): void => {
    if (!saving && !checking) onClose()
  }

  const renderStep = (): ReactElement => {
    if (step === 'queries') {
      return (
        <>
          <DialogContent className='ai-categories-editor__save-dialog-content'>
            <Typography variant='body2'>
              Optionally, enter example queries to test how they would be categorized with your
              unsaved categories. Nothing is saved while testing. Leave these empty to skip testing.
            </Typography>
            {checkFailed && (
              <Banner
                type='error'
                title='Error testing'
                body='Retry, or continue without testing.'
              />
            )}
            <Table<{ id: number; query: string }>
              headers={queryHeaders}
              data={queryInputs.map((query, i) => ({ id: i, query }))}
              divider
              toolbar={
                <TableToolbar
                  actions={
                    <Button
                      variant='outlined'
                      startIcon={<Icon name='add' />}
                      onClick={handleAddQuery}
                      disabled={checking || queryInputs.length >= MAX_TEST_QUERIES}
                    >
                      Add query
                    </Button>
                  }
                />
              }
            />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' color='primary' onClick={handleClose} disabled={checking}>
              Cancel
            </Button>
            {queries.length === 0 ? (
              <Button variant='contained' color='primary' onClick={() => setStep('confirm')}>
                Continue
              </Button>
            ) : (
              <>
                {checkFailed && (
                  <Button variant='outlined' color='primary' onClick={() => setStep('confirm')}>
                    Continue without testing
                  </Button>
                )}
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleRunTest}
                  loading={checking}
                >
                  {checkFailed ? 'Retry' : 'Run test'}
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )
    }

    if (step === 'results' && checkResult) {
      return (
        <>
          <DialogContent className='ai-categories-editor__save-dialog-content'>
            <Banner
              type={checkResult.are_categories_distinct ? 'success' : 'warning'}
              title={
                checkResult.are_categories_distinct
                  ? 'Categories look distinct'
                  : 'Some categories may overlap'
              }
              body={checkResult.categories_distinctness_reasoning.replace(
                /`([^`]+)`/g,
                (_, name: string) => toTitleCase(name),
              )}
            />
            <Table<CategorizedQueryRow>
              headers={categorizedQueryHeaders}
              data={checkResult.categorized_queries.map(([query, category], id) => ({
                id,
                query,
                category,
              }))}
              divider
            />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' color='primary' onClick={handleClose}>
              Close
            </Button>
            <Button variant='outlined' color='primary' onClick={() => setStep('queries')}>
              Edit queries
            </Button>
            <Button variant='contained' color='primary' onClick={() => setStep('confirm')}>
              Continue
            </Button>
          </DialogActions>
        </>
      )
    }

    return (
      <>
        <DialogContent>
          <Typography variant='body2'>
            Saving new categories only affects interactions from this point forward. Past
            interactions keep their current categories — to recategorize them,{' '}
            {canBackfill
              ? 'use the "Backfill categories" button.'
              : 'request a backfill from your Customer Success Manager.'}
            <br />
            <br />
            Health Check questions will automatically be recategorized with these new categories for
            consistency.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='primary' onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => void onSave()}
            loading={saving}
          >
            Save
          </Button>
        </DialogActions>
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      slotProps={{
        // Reset after the close animation finishes
        transition: {
          onExited: () => {
            setStep('queries')
            resetCheck()
          },
        },
      }}
    >
      <DialogTitle className='ai-categories-editor__dialog-title'>
        Save categories
        <Tooltip title='Close'>
          <IconButton
            aria-label='Close'
            size='small'
            onClick={handleClose}
            disabled={saving || checking}
          >
            <Icon name='close' fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      {renderStep()}
    </Dialog>
  )
}

export default AICategoriesSaveDialog
